import {Component, OnInit} from '@angular/core';
import {Cart, CartStatus} from '../../shopping-cart/Cart';
import {CartService, LineItem} from '../../shopping-cart/cart.service';
import {Observable, of, retryWhen, Subscription, throwError, zip} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {DownloadInfoWithLineItem, DownloadService, ItemDownloadInfo} from '../../shopping-cart/download.service';
import {delay, first, map, switchMap, tap} from 'rxjs/operators';
import {LicenseType, Messages, ProductService} from '@uofu-uss-aux/software-catalog-ng-core';
import {UccpConfiguration, UccpService} from '../../payment/uccp.service';
import {NotificationService} from '@uofu-uss/angular-util';
import {DatePipe, NgForOf, NgIf} from '@angular/common';
import {SpinnerComponent} from '../../svg/spinner/spinner.component';
import {MakePaymentComponent} from '../../payment/make-payment/make-payment.component';
import {DownloadListComponent} from '../download-list/download-list.component';
import {TotalComponent} from '../../shopping-cart/total/total.component';
import {CartItemComponent} from '../../shopping-cart/cart-item/cart-item.component';
import {CartStatusBadgeComponent} from '../cart-status-badge/cart-status-badge.component';
import {InstallDirectionsComponent} from '../install-directions/install-directions.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    SpinnerComponent,
    MakePaymentComponent,
    DownloadListComponent,
    TotalComponent,
    CartItemComponent,
    DatePipe,
    NgForOf,
    CartStatusBadgeComponent,
    NgForOf,
    InstallDirectionsComponent
  ]
})
export class OrderSummaryComponent implements OnInit {

  cart: Cart;
  uccpConfig: UccpConfiguration;
  items: ReadonlyArray<LineItem> = [];
  downloads: ReadonlyArray<DownloadInfoWithLineItem> = [];
  submitPayment = false;
  purchaseOrderPending = false;
  hasDownloadAvailableWithoutPurchase = false;
  displayNotes = false;
  hasComments = false;
  hasOslComments = false;

  constructor(private readonly route: ActivatedRoute,
              private readonly cartService: CartService,
              private readonly notificationService: NotificationService,
              private readonly downloadService: DownloadService,
              private readonly productService: ProductService,
              private readonly uccpService: UccpService) {
  }

  ngOnInit(): void {
    const id: number = +this.route.snapshot.queryParams.ID;
    const loadCartObserver = {
      next: this.loadCartSuccess,
      error: this.loadError,
    };
    this.fetchCartWithRetryEligibility(id).pipe(
      switchMap(fetched => fetched.retryNeeded() ? throwError(() => fetched) : of(fetched.cart)),
      retryWhen((errors: Observable<any>) => errors.pipe(
        switchMap(e => e.cart ? of(e).pipe(delay(500)) : throwError(() => e)))
      ),
      first())
      .subscribe(loadCartObserver);
    const loadUccpObserver = {
      next: this.fetchUccpConfigSuccess,
      error: this.checkoutCartError,
    };
    this.uccpService.fetchUccpConfig(id)
      .pipe(first())
      .subscribe(loadUccpObserver);
  }

  fetchCartWithRetryEligibility(id: number): Observable<{ cart: Cart, retryNeeded: () => boolean }> {
    let count = 0;
    return this.cartService.fetchCartWithId(id).pipe(
      tap(() => count++),
      map(cart => {
        return {
          cart,
          retryNeeded: () => !this.isValidCartStatus(cart) && count <= 5
        };
      }));
  }

  swapToCreditCard(cart: Cart): Subscription {
    if (this.purchaseOrderPending) {
      const observer = {
        next: this.checkoutCartSuccess,
        error: this.checkoutCartError,
      };
      return this.cartService.checkout(cart)
        .pipe(first())
        .subscribe(observer);
    } else {
      this.notificationService.addError({
        title: 'Error',
        body: 'Unable to switch to Credit Card payment',
      });
    }
  }

  private readonly checkoutCartError = (errorResponse: HttpErrorResponse) => {
    const body = Messages.validationMsg(errorResponse, 'Unable to switch to Credit Card payment');
    this.notificationService.addError({
      title: 'Error',
      body,
    });
  }

  private readonly checkoutCartSuccess = () => {
    this.submitPayment = true;
  }

  private readonly fetchUccpConfigSuccess = (config: UccpConfiguration) => {
    this.uccpConfig = config;
  }

  private readonly fetchDownloads = (cart: Cart) => {
    return cart.status === CartStatus.Purchased ? this.downloadService.fetchAvailableByCart(cart.id) : of([]);
  }

  private readonly loadError = () => {
    console.trace('Error loading cart');
    this.downloads = [];
    this.notificationService.addError('Unable to load order summary');
  }

  private readonly loadCartSuccess = (cart: Cart) => {
    this.purchaseOrderPending = cart.status === CartStatus.POPending;
    this.cart = cart;
    if (!this.isValidCartStatus(cart)) {
      this.notificationService.addError('There was a problem with checkout. Please contact support to resolve the issue.');
    }
    const observer = {
      next: ([lineItems, downloads]) => this.loadItemsSuccess(cart, lineItems, downloads),
      error: this.loadError,
    };
    zip(
      this.cartService.fetchLineItems(cart),
      this.fetchDownloads(cart)
    )
      .pipe(first())
      .subscribe(observer);
  }

  private readonly loadItemsSuccess = (cart: Cart, lineItems: readonly LineItem[], downloads: readonly ItemDownloadInfo[]) => {
    this.items = lineItems || [];
    this.hasDownloadAvailableWithoutPurchase = !!this.items.find(item => item.offering.downloadAvailableWithoutPurchase);
    this.hasComments = !!cart.comments && this.cartContainsUniversityLicenses(lineItems);
    this.hasOslComments = !!cart.publicOslComments;
    this.displayNotes = this.hasComments || this.hasOslComments;
    if (cart.status === CartStatus.Purchased && this.missingSerialNumbers(this.items)) {
      this.notificationService.addWarning('There are fewer serial numbers than expected. ' +
        'Please contact support to resolve the issue.');
    }
    this.downloads = this.downloadService.combineDownloadsWithLineItem(lineItems, downloads);
  }

  private isValidCartStatus(cart: Cart): boolean {
    return !(cart.status === CartStatus.SessionCart || cart.status === CartStatus.WishList || cart.status === CartStatus.Hold);
  }

  private missingSerialNumbers(lineItems: readonly LineItem[]): boolean {
    return !!lineItems.find(item =>
      item.offering.serialPoolId &&
      (!item.cartItem.serialNumbers ||
        item.cartItem.serialNumbers.length < item.cartItem.quantity ||
        item.cartItem.serialNumbers.find(sn => sn.startsWith('Unable to assign a license Number')))
    );
  }

  cartContainsUniversityLicenses(lineItems: ReadonlyArray<LineItem>) {
    const item = lineItems?.length && lineItems[0];
    return !!item && this.productService.findLicenseFromOffering(item.product, item.offering)?.type === LicenseType.University;
  }

  get hasItems(): boolean {
    return !!this.items.length;
  }

  get itemsWithPositiveQuantity(): ReadonlyArray<LineItem> {
    return this.items.filter(item => item.cartItem.quantity > 0);
  }

  printPage(): void {
    window.print();
  }

}
